import { ArrowLink } from "@components/ArrowLink"
import { Breadcrumbs } from "@components/Breadcrumbs"
import { LinkButton } from "@components/Buttons"
import Footer from "@components/Footer"
import { useIntl } from "@components/Intl"
import { Box, Container } from "@components/Layout"
import SEO from "@components/SEO"
import { Paragraph, TitleXL } from "@components/Typography"
import { getKontentImage } from "@lib/kontent"
import { Item, SingleItemResponse } from "@lib/kontent/fetchContent"
import { CalloutCardCollectionDetailPage } from "@lib/kontent/models/CalloutCardCollectionDetailPage"
import { CollectionDetailPage } from "@lib/kontent/models/CollectionDetailPage"
import ArticleCombinationGrid from "@screens/collection/detail/ArticleCombinationGrid"
import ArticleGrid from "@screens/collection/detail/ArticleGrid"
import React from "react"

interface Props {
  uid: string
  collection: Item<CollectionDetailPage>
  modularContent?: SingleItemResponse["modular_content"]
}

export const separateUids = ["gemstones", "frames", "bases"]
export const combinationUids = ["rings", "bracelets", "necklaces", "earrings"]

const CollectionDetail: React.FC<Props> = ({
  uid,
  collection,
  modularContent,
}) => {
  const { messages } = useIntl()
  const hasImage = collection.elements.featured_image.value[0]?.url
  const prefix = messages.navigation[uid].url
  const hasCalloutCards = collection.elements.callout_cards.value.length > 0
  let callOutCards: CalloutCardCollectionDetailPage[] = []

  if (hasCalloutCards) {
    callOutCards = collection.elements.callout_cards.value.map(
      (id) => modularContent[id].elements as CalloutCardCollectionDetailPage
    )
  }

  return (
    <>
      <SEO
        title={collection.elements.seo__meta_title.value}
        description={collection.elements.seo__meta_description.value}
        imageUrl={collection.elements.seo__meta_image.value[0]?.url}
        urlKey={uid}
      />
      <Container maxWidth="109rem" mt={["5rem", "5rem", "6rem"]}>
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          position="relative"
          alignItems="flex-start"
        >
          <Box py={["4rem", "3rem"]}>
            <Box>
              {separateUids.includes(uid) ? (
                <Breadcrumbs
                  items={[
                    {
                      href: messages.navigation.collection.url,
                      as: messages.navigation.collection.url,
                      label: messages.navigation.collection.title,
                    },
                    {
                      href: messages.navigation.separateItems.url,
                      as: messages.navigation.separateItems.url,
                      label: messages.navigation.separateItems.title,
                    },
                    {
                      label: collection.elements.title.value,
                    },
                  ]}
                />
              ) : (
                <Box display={["none", "block"]}>
                  <ArrowLink
                    variant="arrow-left"
                    href={messages.navigation.collection.url}
                    as={messages.navigation.collection.url}
                  >
                    {messages.navigation.collection.title}
                  </ArrowLink>
                </Box>
              )}
            </Box>
            <Box maxWidth={"30rem"} py={4}>
              <TitleXL>{collection.elements.title.value}</TitleXL>
              <Paragraph>{collection.elements.description.value}</Paragraph>
              {collection.elements.link_file.value &&
                collection.elements.link_title.value && (
                  <LinkButton
                    as="link"
                    href={collection.elements.link_file.value[0].url}
                    target="_blank"
                  >
                    {collection.elements.link_title.value}
                  </LinkButton>
                )}
            </Box>
          </Box>
          <Box
            display={["none", "none", "block"]}
            position="absolute"
            right="0"
            height="100%"
            width="50%"
            maxWidth="calc(100% - 34rem)"
            top="0"
          >
            {hasImage && (
              <img
                src={getKontentImage(
                  collection.elements.featured_image.value[0].url,
                  {
                    width: 1200,
                  }
                )}
                alt="background"
                style={{ height: "100%", width: "100%", objectFit: "cover" }}
              />
            )}
          </Box>
        </Box>

        {combinationUids.includes(uid) ? (
          <ArticleCombinationGrid
            uid={uid}
            prefix={prefix}
            href={messages.navigation.collectionDetail.url}
            callOutCards={callOutCards}
          />
        ) : (
          <ArticleGrid
            uid={uid}
            prefix={prefix}
            href={messages.navigation.separateItemsProductDetail.url}
            title={collection.elements.title.value}
          />
        )}
      </Container>
      <Footer />
    </>
  )
}

export default CollectionDetail
